import {
  ChangeDetectorRef, Component, OnInit,
} from '@angular/core';
import {
  ActivatedRoute, Router,
} from '@angular/router';
import { NotificationService } from '../../../application/services/NotificationService';
import { HttpResponse } from '../../../application/types/HttpResponse';
import { TeamApiService } from '../../services/team-api/TeamApiService';
import {
  TeamDetails,
  TeamEventSummary,
} from '../../types/Event';
import { resolveTeamLogoBackground } from '../../utils/resolveTeamLogoUrl';

@Component({
  selector: 'app-team-view-page',
  standalone: false,
  templateUrl: './team-view-page.component.html',
  styleUrl: './team-view-page.component.sass',
})
export class TeamViewPageComponent implements OnInit {
  public team: TeamDetails | null = null;
  public selectedSeason = 'all';
  public loading = false;
  public errorMessage: string | null = null;

  public constructor(
    private route: ActivatedRoute,
    private router: Router,
    private teamApi: TeamApiService,
    private notificationService: NotificationService,
    private changeDetector: ChangeDetectorRef,
  ) { }

  public async ngOnInit(): Promise<void> {
    const teamSlug = this.route.snapshot.paramMap.get('teamSlug');
    if (!teamSlug) {
      this.errorMessage = 'Команда не найдена.';
      return;
    }

    await this.loadTeam(teamSlug);
  }

  public get seasons(): string[] {
    return [
      ...new Set(this.team?.events.map((event) => event.seasonName) ?? []),
    ].sort().reverse();
  }

  public get selectedEvents(): TeamEventSummary[] {
    if (!this.team || this.selectedSeason === 'all') {
      return this.team?.events ?? [];
    }

    return this.team.events.filter((event) => event.seasonName === this.selectedSeason);
  }

  public get soldTicketsTotal(): number {
    return this.selectedEvents.reduce((total, event) => total + event.soldTicketsTotal, 0);
  }

  public get regularSoldTicketsTotal(): number {
    return this.selectedEvents
      .filter((event) => event.seasonType === 'regular')
      .reduce((total, event) => total + event.soldTicketsTotal, 0);
  }

  public get playoffSoldTicketsTotal(): number {
    return this.selectedEvents
      .filter((event) => event.seasonType === 'playoff')
      .reduce((total, event) => total + event.soldTicketsTotal, 0);
  }

  public get revenueTotal(): number {
    return this.selectedEvents.reduce((total, event) => total + event.soldRevenue, 0);
  }

  public get mostTicketsEvent(): TeamEventSummary | null {
    return this.getBestEvent('tickets');
  }

  public get mostRevenueEvent(): TeamEventSummary | null {
    return this.getBestEvent('revenue');
  }

  public getLogoBackground(): string | null {
    return this.team ? resolveTeamLogoBackground(this.team.logoUrl) : null;
  }

  public async deleteTeam(): Promise<void> {
    if (!this.team || !window.confirm(`Удалить команду "${this.team.name}"?`)) {
      return;
    }

    try {
      const response = await this.teamApi.deleteTeam(this.team.id);
      if (response.status === 'failed') {
        this.errorMessage = response.errorMessage;
        return;
      }

      this.notificationService.notify('directory', 'Команда удалена', this.team.name);
      await this.router.navigate([
        '/dashboard/teams',
      ]);
    } catch (error) {
      this.errorMessage = error instanceof Error ? error.message : 'Не удалось удалить команду.';
    } finally {
      this.changeDetector.detectChanges();
    }
  }

  public formatDate(value: string): string {
    return new Intl.DateTimeFormat('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(value));
  }

  public formatMoney(value: number): string {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
      maximumFractionDigits: 0,
    }).format(value);
  }

  public formatNumber(value: number | null): string {
    return value === null ? 'Не указана' : new Intl.NumberFormat('ru-RU').format(value);
  }

  public getEventName(event: TeamEventSummary): string {
    return `${event.homeTeam.name} - ${event.awayTeam.name}`;
  }

  private getBestEvent(metric: 'tickets' | 'revenue'): TeamEventSummary | null {
    const events = this.selectedEvents;
    if (events.length === 0) {
      return null;
    }

    return events.reduce((best, event) => {
      const bestValue = metric === 'tickets' ? best.soldTicketsTotal : best.soldRevenue;
      const eventValue = metric === 'tickets' ? event.soldTicketsTotal : event.soldRevenue;
      return eventValue > bestValue ? event : best;
    });
  }

  private async loadTeam(teamId: string): Promise<void> {
    this.loading = true;
    this.errorMessage = null;

    try {
      const response = await this.teamApi.getTeam(teamId);
      const team = this.unwrapResponse(response);
      if (!team) {
        this.errorMessage = 'Команда не найдена.';
        return;
      }

      this.team = team;
    } catch (error) {
      this.errorMessage = error instanceof Error ? error.message : 'Не удалось загрузить команду.';
    } finally {
      this.loading = false;
      this.changeDetector.detectChanges();
    }
  }

  private unwrapResponse<ResponseType>(response: HttpResponse<ResponseType>): ResponseType | null {
    if (response.status === 'failed') {
      this.errorMessage = response.errorMessage;
      return null;
    }

    return response.response;
  }
}
